import {
  LEGACY_ROTA_TO_PERMISSION,
  PDC_FUNCIONALIDADE_TO_PERMISSION,
} from 'utils/portalPermissionConfig';

type LoginPermissionDto = {
  Rota?: string;
};

type LoginDivisionDto = {
  DivisaoID?: string | number;
  NomeDivisao?: string;
  QtdFrotas?: string | number;
  NaoAtribuido?: string;
};

type LoginGroupDto = {
  GrupoID?: string | number;
  Nome?: string;
  Divisao?: LoginDivisionDto[];
};

type LoginUsuarioVetorDto = {
  Nome?: string;
  Email?: string;
  Celular?: string;
  GestorID?: string | number;
  CPF?: string;
  GrupoIDAtual?: string | number;
  DivisaoIDAtual?: string | number;
  Grupos?: LoginGroupDto[];
  GEID?: string;
  Abreviatura?: string;
  Permissoes?: LoginPermissionDto[];
  GrupoAtual?: LoginGroupDto;
  FirstAccess?: string | boolean;
  ProvisionalPassword?: string | boolean;
};

type LoginCostCenterMeasurement = {
  costCenter?: string;
  nome?: string;
  emails?: string[];
};

type LoginCostCenterGroup = {
  subGrupoId?: string;
  costCenters?: LoginCostCenterMeasurement[];
};

export type LoginApiResponse = {
  accessToken?: string;
  refreshToken?: string;
  titulo?: string;
  logo?: string;
  cor?: string;
  etapaOnboarding?: number;
  dashboardConfig?: { sections?: unknown } | null;
  primeirosAcessosUsuario?: string[];
  usuarioVetor?: LoginUsuarioVetorDto;
  CostCenters?: LoginCostCenterGroup[];
  ultimoAcessoAnterior?: string | null;
};

const normalizeYesNoFlag = (value: string | boolean | null | undefined) => {
  if (value === true || value === 'S' || value === 's' || value === 'true')
    return 'S';
  if (value === false || value === 'N' || value === 'n' || value === 'false')
    return 'N';
  return value ? 'S' : 'N';
};

const mapDivisions = (divisions?: LoginDivisionDto[]): Division[] =>
  (divisions || []).map(dto => ({
    divisionId: dto.DivisaoID?.toString() || '',
    divisionName: dto.NomeDivisao || '',
    fleetQtt: dto.QtdFrotas?.toString() || '',
    notAssigned: dto.NaoAtribuido || '',
  }));

const mapGroups = (groups?: LoginGroupDto[]): Group[] =>
  (groups || []).map(dto => ({
    groupId: dto.GrupoID?.toString() || '',
    groupName: dto.Nome || '',
    groupDivision: mapDivisions(dto.Divisao),
  }));

export const mapPortalPermissions = (
  permissionDto?: LoginPermissionDto[],
): Permission[] => {
  const permissions = new Set<Permission>();

  permissionDto?.forEach(item => {
    const rota = item.Rota?.trim();
    if (!rota) return;

    const mapped =
      (PDC_FUNCIONALIDADE_TO_PERMISSION as Record<string, Permission>)[rota] ||
      (LEGACY_ROTA_TO_PERMISSION as Record<string, Permission>)[rota];

    if (mapped) permissions.add(mapped);
  });

  return Array.from(permissions);
};

const mapCostCenters = (centers?: LoginCostCenterGroup[]): CostCenter[] =>
  (centers || []).map(center => ({
    groupId: center.subGrupoId || '',
    costCenters:
      center.costCenters?.map(costCenter => ({
        id: costCenter.costCenter || '',
        name: costCenter.nome || '',
        emails: costCenter.emails || [],
      })) || [],
  }));

export const mapLoginResponseToUser = (userDto: LoginApiResponse): User => {
  const usuarioVetor = userDto.usuarioVetor;

  return {
    name: usuarioVetor?.Nome || '',
    email: usuarioVetor?.Email || '',
    phone: usuarioVetor?.Celular || '',
    managerId: usuarioVetor?.GestorID?.toString() || '',
    cpf: usuarioVetor?.CPF || '',
    currentGroupId: usuarioVetor?.GrupoIDAtual?.toString() || '',
    currentDivisionId: usuarioVetor?.DivisaoIDAtual?.toString() || '',
    groups: mapGroups(usuarioVetor?.Grupos),
    geid: usuarioVetor?.GEID || '',
    abbreviation: usuarioVetor?.Abreviatura || '',
    permissions: mapPortalPermissions(usuarioVetor?.Permissoes),
    currentGroup: {
      currentGroupId: usuarioVetor?.GrupoAtual?.GrupoID?.toString() || '',
      currentGroupName: usuarioVetor?.GrupoAtual?.Nome || '',
      currentGroupDivision: mapDivisions(usuarioVetor?.GrupoAtual?.Divisao),
    },
    dashboardConfig: userDto.dashboardConfig
      ? (userDto.dashboardConfig.sections as dashboardConfig)
      : null,
    token: userDto.accessToken || '',
    refreshToken: userDto.refreshToken || null,
    logo: userDto.logo || null,
    title: userDto.titulo || null,
    color: userDto.cor || null,
    step: userDto.etapaOnboarding ?? null,
    accessedAreas: userDto.primeirosAcessosUsuario || [],
    provisionalPassword: normalizeYesNoFlag(
      usuarioVetor?.ProvisionalPassword,
    ),
    firstAccess: normalizeYesNoFlag(usuarioVetor?.FirstAccess),
    costCenters: mapCostCenters(userDto.CostCenters),
    onbFAQ: null,
    onbFin: null,
    onbFines: null,
    onbSchedule: null,
    onbVehicle: null,
    onbLogs: null,
    onbModels: null,
    onbFleetDist: null,
    onbEntExit: null,
    onbServices: null,
    onbDocuments: null,
    isMediator: false,
    success: true,
    totalErrors: null,
    msnErrors: null,
    mainScreen: null,
    notification: [],
    ultimoAcessoAnterior: userDto.ultimoAcessoAnterior ?? null,
  };
};
